import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createSocialPost, deleteSocialPost, fetchTeamRoomMembership, fetchTeamRoomPrompts, fetchTeamSocialPosts, joinTeamRoom, voteTeamRoomPrompt } from "../api";
import { useAuth } from "./AuthGate";
import SocialPostCard, { postIdOf } from "./SocialPostCard";
import { usePreferences } from "../context/PreferencesContext";
import styles from "../styles/ClubRoomSection.module.css";

const normalize = (value) => String(value || "").trim().toLowerCase();

export const getClubRoomCopy = (sport) => {
  const key = normalize(sport).replace(/[-\s]/g, "_");
  if (key === "football" || key === "soccer") {
    return {
      room: "Terrace",
      talk: "Terrace talk",
      join: "Join Terrace",
      joined: "Joined Terrace",
      placeholder: (team) => `What are ${team} fans saying?`,
    };
  }
  if (key === "baseball" || key === "mlb") {
    return {
      room: "Clubhouse",
      talk: "Clubhouse chatter",
      join: "Join Clubhouse",
      joined: "Joined Clubhouse",
      placeholder: () => "What's the clubhouse saying?",
    };
  }
  if (key === "basketball" || key === "nba") {
    return {
      room: "Court",
      talk: "Court talk",
      join: "Join Court",
      joined: "Joined Court",
      placeholder: (team) => `What are ${team} fans saying?`,
    };
  }
  if (key === "nfl" || key === "american_football") {
    return {
      room: "Locker Room",
      talk: "Locker room",
      join: "Join Locker Room",
      joined: "Joined Locker Room",
      placeholder: (team) => `What are ${team} fans saying?`,
    };
  }
  if (key === "hockey" || key === "nhl") {
    return {
      room: "Rink",
      talk: "Rink talk",
      join: "Join Rink",
      joined: "Joined Rink",
      placeholder: (team) => `What are ${team} fans saying?`,
    };
  }
  if (key === "tennis") {
    return {
      room: "Baseline",
      talk: "Baseline talk",
      join: "Join Baseline",
      joined: "Joined Baseline",
      placeholder: () => "Start the baseline chat...",
    };
  }
  return {
    room: "Clubhouse",
    talk: "Clubhouse chatter",
    join: "Join Clubhouse",
    joined: "Joined Clubhouse",
    placeholder: () => "Start the conversation...",
  };
};

const toPosts = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.posts)) return payload.posts;
  return [];
};

const roomGlyph = (sport) => {
  const key = normalize(sport).replace(/[-\s]/g, "_");
  if (key === "baseball" || key === "mlb") return "◆";
  if (key === "basketball" || key === "nba") return "◉";
  if (key === "nfl" || key === "american_football") return "⬡";
  if (key === "hockey" || key === "nhl") return "✦";
  if (key === "tennis") return "◌";
  return "◍";
};

export default function ClubRoomSection({ teamName, teamLogo = "", sport = "football", leagueKey = "", leagueName = "", opponentName = "", away = false, dark = true }) {
  const { user, loginWithGoogle } = useAuth();
  const {
    favoriteSports,
    favoriteTeams,
    favoriteLeagues,
    updatePreferences,
    saving: prefsSaving,
  } = usePreferences();
  const copy = useMemo(() => getClubRoomCopy(sport), [sport]);
  const [posts, setPosts] = useState([]);
  const [memberCount, setMemberCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState("");
  const [membership, setMembership] = useState(null);
  const [roomPrompts, setRoomPrompts] = useState([]);

  const joined = membership?.is_member ?? (favoriteTeams || []).some((team) => normalize(team) === normalize(teamName));

  const loadPosts = useCallback(async () => {
    if (!teamName) return;
    setLoading(true);
    setErr("");
    try {
      const payload = await fetchTeamSocialPosts(teamName, { sport, limit: 40, since_days: 180 });
      setPosts(toPosts(payload));
      setMemberCount(Number(payload?.member_count ?? 0) || 0);
    } catch (error) {
      setPosts([]);
      setMemberCount(null);
      setErr(error?.response?.data?.detail || "Could not load fan posts.");
    } finally {
      setLoading(false);
    }
  }, [sport, teamName]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  useEffect(() => {
    if (!teamName) return;
    Promise.allSettled([
      fetchTeamRoomMembership(teamName, { sport, league_key: leagueKey, league_name: leagueName }),
      fetchTeamRoomPrompts(teamName, { sport, league_key: leagueKey, league_name: leagueName }),
    ]).then(([memberResult, promptResult]) => {
      if (memberResult.status === "fulfilled") setMembership(memberResult.value);
      if (promptResult.status === "fulfilled") setRoomPrompts(promptResult.value?.prompts || []);
    });
  }, [leagueKey, leagueName, sport, teamName]);

  const joinRoom = async () => {
    if (!user) {
      loginWithGoogle?.();
      return;
    }
    if (joined) return;
    if (!leagueKey) {
      setErr("This club’s league is still being resolved. Please try again shortly.");
      return;
    }
    const value = await joinTeamRoom(teamName, { sport, league_key: leagueKey, league_name: leagueName });
    setMembership(value);
    if (!(favoriteTeams || []).some((team) => normalize(team) === normalize(teamName))) {
      await updatePreferences({ favoriteSports, favoriteTeams: [...(favoriteTeams || []), teamName], favoriteLeagues });
    }
  };

  const submitPost = async (evt) => {
    evt.preventDefault();
    const trimmed = body.trim().slice(0, 280);
    if (!trimmed || !user || !joined) return;
    setPosting(true);
    setErr("");
    try {
      await createSocialPost({
        body: trimmed,
        visibility: "public",
        tags: [
          {
            tag_type: "team",
            value: teamName,
            team_name: teamName,
            sport,
          },
        ],
      });
      setBody("");
      await loadPosts();
    } catch (error) {
      setErr(error?.response?.data?.detail || "Could not post.");
    } finally {
      setPosting(false);
    }
  };

  const vote = async (promptId, optionId) => {
    if (!joined) { setErr(`Join the ${copy.room} to vote.`); return; }
    try {
      const updated = await voteTeamRoomPrompt(promptId, optionId);
      setRoomPrompts((items) => items.map((item) => item.id === promptId ? updated : item));
    } catch (error) { setErr(error?.response?.data?.detail || "Could not record your vote."); }
  };

  const deletePost = async (postId) => {
    if (!postId) return;
    try {
      await deleteSocialPost(postId);
      setPosts((prev) => prev.filter((post) => String(postIdOf(post)) !== String(postId)));
    } catch (error) {
      setErr(error?.response?.data?.detail || "Could not delete post.");
    }
  };

  const prompts = [
    "Lineup thoughts?",
    "Score prediction",
    "Transfer shout",
    opponentName ? `Mood before ${away ? "@" : "vs"} ${opponentName}?` : "",
  ].filter(Boolean);

  return (
    <section className={`${styles.room} ${dark ? styles.dark : styles.light}`}>
      <header className={styles.hero}>
        <div className={styles.heroGlow} aria-hidden="true" />
        <div className={styles.identity}>
          <div className={styles.crest}>
            {teamLogo ? <img src={teamLogo} alt="" /> : <span>{teamName?.[0]?.toUpperCase() || roomGlyph(sport)}</span>}
          </div>
          <div>
            <div className={styles.eyebrow}><span className={styles.liveDot} /> LIVE FAN SPACE</div>
            <h2>{teamName} {copy.room}</h2>
            <p>{copy.talk} for the people who follow every moment.</p>
          </div>
        </div>
        <button
          type="button"
          onClick={joinRoom}
          disabled={prefsSaving || joined}
          className={`${styles.joinButton} ${joined ? styles.joined : ""}`}
        >
          {!user ? copy.join : joined ? `✓ ${copy.joined}` : prefsSaving ? "Joining..." : `+ ${copy.join}`}
        </button>
        <div className={styles.metrics}>
          <div><strong>{memberCount ?? "—"}</strong><span>Members</span></div>
          <div><strong>{posts.length}</strong><span>Posts</span></div>
          <div><strong>{roomPrompts.length}</strong><span>Live polls</span></div>
        </div>
      </header>

      {roomPrompts.length ? (
        <section className={styles.pulseSection}>
          <div className={styles.sectionHeading}>
            <div><span className={styles.eyebrow}>MATCHDAY PULSE</span><h3>What fans are deciding</h3></div>
            <span className={styles.openBadge}>Open now</span>
          </div>
          <div className={styles.pollGrid}>
          {roomPrompts.map((prompt) => (
            <article key={prompt.id} className={styles.pollCard}>
              <strong>{prompt.question}</strong>
              <div className={styles.pollOptions}>
                {(prompt.options || []).map((option) => (
                  <button key={option.id} type="button" disabled={!joined} onClick={() => vote(prompt.id, option.id)} className={option.selected_by_me ? styles.pollSelected : ""}>
                    {option.label} · {option.vote_count || option.votes || 0}
                  </button>
                ))}
              </div>
            </article>
          ))}
          </div>
        </section>
      ) : null}

      <section className={styles.conversation}>
        <div className={styles.sectionHeading}>
          <div><span className={styles.eyebrow}>JOIN IN</span><h3>Start a conversation</h3></div>
          <span className={styles.visibility}>◎ Public room</span>
        </div>
        <div className={styles.promptRail}>
          {prompts.map((prompt) => (
            <button key={prompt} type="button" onClick={() => setBody((prev) => (prev.trim() ? `${prev.trim()} ${prompt}` : prompt).slice(0, 280))}>
              {prompt}<span>↗</span>
            </button>
          ))}
        </div>

      {!user ? (
        <div className={styles.gate}>
          <div><strong>Sign in to join the {copy.room}</strong><span>Read freely, then sign in to post, react and vote.</span></div>
          <button type="button" onClick={() => loginWithGoogle?.()}>Sign in to CSB</button>
        </div>
      ) : !joined ? (
        <div className={styles.gate}>
          <div><strong>Your club. Your people.</strong><span>Join the {copy.room} to post, comment and vote. Browsing stays open.</span></div>
          <button type="button" onClick={joinRoom}>Join now</button>
        </div>
      ) : (
        <form onSubmit={submitPost} className={styles.composer}>
          <div className={styles.composerAvatar}>{user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "F"}</div>
          <div className={styles.composerMain}>
          <textarea
            value={body}
            maxLength={280}
            onChange={(event) => setBody(event.target.value)}
            placeholder={copy.placeholder(teamName)}
            rows={3}
          />
          <div className={styles.composerFooter}>
            <span>{body.length}/280</span>
            <button type="submit" disabled={posting || !body.trim()}>
              {posting ? "Posting..." : "Post"}
            </button>
          </div>
          </div>
        </form>
      )}
      </section>

      {err ? <div className={styles.error}>{err}</div> : null}

      <section className={styles.feedSection}>
        <div className={styles.sectionHeading}>
          <div><span className={styles.eyebrow}>FROM THE FANS</span><h3>Latest in the {copy.room}</h3></div>
          <span className={styles.feedCount}>{posts.length} posts</span>
        </div>
        <div className={styles.feed}>
        {loading ? <div className={styles.loading}>Loading the conversation…</div> : null}
        {!loading && !posts.length ? (
          <div className={styles.empty}><span>◎</span><strong>Be the first voice</strong><p>Start today’s {teamName} conversation.</p></div>
        ) : null}
        {posts.map((post, idx) => (
          <SocialPostCard key={postIdOf(post) || idx} post={post} onDelete={deletePost} dark={dark} />
        ))}
        </div>
      </section>
    </section>
  );
}
